import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const milestones = [
  {
    year: '1992',
    title: 'The Beginning',
    desc: 'Manmohan Sharma founded Bollywood Firki in Jaipur, starting with folk and live music recordings including Maithili, Awadhi, and Bhojpuri genres.',
    side: 'left',
  },
  {
    year: '2000',
    title: 'Digital Expansion',
    desc: 'Bollywood Firki embraced the digital revolution, expanding into CD and digital audio formats, reaching audiences across North India.',
    side: 'right',
  },
  {
    year: '2010',
    title: 'YouTube Journey Begins',
    desc: 'Launched the first YouTube channels, pioneering digital music distribution for regional and Hindi Devotional content in India.',
    side: 'left',
  },
  {
    year: '2018',
    title: 'Network of 50+ Channels',
    desc: 'Grew to over 50 YouTube channels covering Bhakti, Folk, Nautanki, Bhojpuri, and Hindi Devotional content with millions of subscribers.',
    side: 'right',
  },
  {
    year: '2022',
    title: '50+ YouTube Awards',
    desc: 'Received over 50 Gold and Silver Play Buttons from YouTube, cementing status as India\'s most awarded regional music network.',
    side: 'left',
  },
  {
    year: '2024',
    title: '100M+ Milestone',
    desc: 'Crossed 100 million total subscribers and 100 billion views across the network — a historic milestone for Indian regional music.',
    side: 'right',
  },
];

const stats = [
  { icon: 'ri-team-line', value: '70+', label: 'Team Members' },
  { icon: 'ri-youtube-line', value: '70+', label: 'YouTube Channels' },
  { icon: 'ri-music-2-line', value: '30+', label: 'Years Experience' },
  { icon: 'ri-award-line', value: '50+', label: 'YouTube Awards' },
];

export default function AboutPage() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    document.title = 'About Us – Bollywood Firki | India\'s Music Legacy Since 1992';

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'page-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://bollywoodfirki.com',
      '@type': 'AboutPage',
      name: 'About Bollywood Firki – India\'s Music Legacy Since 1992',
      url: `${import.meta.env.VITE_SITE_URL || ''}/about`,
      description: 'Founded in 1992 by Manmohan Sharma, Bollywood Firki (Bollywood Firki) has grown from a folk music label to India\'s largest YouTube music network with 100M+ subscribers.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || ''}/` },
          { '@type': 'ListItem', position: 2, name: 'About Us', item: `${import.meta.env.VITE_SITE_URL || ''}/about` },
        ],
      },
      mainEntity: {
        '@type': 'Organization',
        name: 'Bollywood Firki',
        alternateName: 'Bollywood Firki',
        foundingDate: '1992',
        founder: {
          '@type': 'Person',
          name: 'Manmohan Sharma',
          jobTitle: 'Founder & Chairman',
        },
        numberOfEmployees: { '@type': 'QuantitativeValue', value: 70 },
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'B-40 DSIDC Complex, Kirti Nagar',
          addressLocality: 'New Delhi',
          postalCode: '110015',
          addressCountry: 'IN',
        },
      },
    });
    const existing = document.getElementById('page-jsonld');
    if (existing) existing.remove();
    document.head.appendChild(script);

    const t = setTimeout(() => setVisible(true), 100);
    return () => {
      clearTimeout(t);
      const el = document.getElementById('page-jsonld');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="bg-[#0d0d0d] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            alt="Bollywood Firki Story"
            className="w-full h-full object-cover object-top"
            src="/images/Director Photo (Manmohan Sharma).png"
                  onError={(e) => ((e.target as HTMLImageElement).src = "/images/default.jpg")}          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pb-16 pt-32 w-full">
          <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">Our Story</span>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mt-3 max-w-3xl leading-tight">
            Building India's Music Legacy <span className="text-[#d4af37]">Since 1992</span>
          </h1>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-14 items-center">
            <div className="lg:w-1/2">
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  alt="Manmohan Sharma - Founder"
                  className="w-full h-full object-cover object-top"
                  src="/images/Director Photo (Manmohan Sharma).png"
                  onError={(e) => ((e.target as HTMLImageElement).src = "/images/default.jpg")}
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                  <div className="text-white font-bold text-xl font-heading">Manmohan Sharma</div>
                  <div className="text-[#d4af37] text-sm">Founder, Bollywood Firki</div>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2">
              <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">The Founder's Vision</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mt-3 mb-6">A Dream Born in Jaipur</h2>
              <div className="space-y-4 text-white/70 text-base leading-relaxed">
                <p>
                  In 1992, <strong className="text-white">Manmohan Sharma</strong> had a vision — to preserve and amplify the rich musical heritage of India. Starting from a modest studio in Jaipur, he began recording folk and live music that resonated with the soul of rural India.
                </p>
                <p>
                  What began as <strong className="text-white">Bollywood Firki</strong>, recording Maithili, Awadhi, Bhojpuri, and Hindi Devotional music, grew into a powerhouse that would redefine Indian regional music. The name "Ganga" was chosen to represent the sacred river — a source of life, culture, and spiritual nourishment.
                </p>
                <p>
                  Today, <strong className="text-white">Bollywood Firki</strong> stands as India's most recognized music label with over{' '}
                  <strong className="text-[#d4af37]">100 million YouTube subscribers</strong> and{' '}
                  <strong className="text-[#d4af37]">100+ billion views</strong> — a testament to Manmohan Sharma's unwavering belief in the power of authentic Indian music.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <div key={stat.label} className="glass-card rounded-2xl p-6 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl mx-auto mb-4">
                  <i className={`${stat.icon} text-3xl text-[#d4af37]`}></i>
                </div>
                <div className="text-3xl md:text-4xl font-bold font-heading text-white mb-1">{stat.value}</div>
                <div className="text-white/70 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-14">
            <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">Our Journey</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mt-3 mb-4">Growth Milestones</h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto rounded-full"></div>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#d4af37]/30 -translate-x-1/2 hidden md:block"></div>
            <div className="space-y-8">
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className={`flex flex-col md:flex-row gap-6 items-center ${m.side === 'right' ? 'md:flex-row-reverse' : 'md:flex-row'}`}
                >
                  <div className={`md:w-5/12 ${m.side === 'right' ? 'md:text-left' : 'md:text-right'}`}>
                    <div className="glass-card rounded-2xl p-6">
                      <div className="text-[#d4af37] text-sm font-bold mb-2">{m.year}</div>
                      <h3 className="text-white font-bold text-lg font-heading mb-2">{m.title}</h3>
                      <p className="text-white/60 text-sm leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex md:w-2/12 justify-center">
                    <div className="w-10 h-10 rounded-full border-2 border-[#d4af37] bg-[#0d0d0d] flex items-center justify-center z-10">
                      <div className="w-3 h-3 rounded-full bg-[#d4af37]"></div>
                    </div>
                  </div>
                  <div className="md:w-5/12 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-14">
            <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">Our Purpose</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mt-3">Vision &amp; Mission</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="glass-card rounded-3xl p-10">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#d4af37]/10 mb-6">
                <i className="ri-eye-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-white/70 leading-relaxed">
                To be the world's most trusted platform for Indian regional music — preserving cultural heritage while embracing digital innovation. We envision a world where every Indian musical tradition finds its global audience through the power of technology and storytelling.
              </p>
            </div>
            <div className="rounded-3xl p-10 bg-[#d4af37]">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-black/20 mb-6">
                <i className="ri-rocket-line text-3xl text-black"></i>
              </div>
              <h3 className="font-heading text-2xl font-bold text-black mb-4">Our Mission</h3>
              <p className="text-black/80 leading-relaxed">
                To empower artists with world-class music production, digital distribution, and promotional support. We are committed to amplifying authentic Indian voices — from Bhakti to Bhojpuri — and connecting them with audiences across the globe through our 70+ channel YouTube network.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-4xl font-bold text-white mb-5">
            Be Part of the <span className="text-[#d4af37]">Legacy</span>
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Join India's most powerful music network and let your talent shine on the world stage.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full text-base font-semibold text-black bg-[#d4af37] whitespace-nowrap transition-all duration-300 hover:scale-105 cursor-pointer"
          >
            Get In Touch <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
